import { fetchGHGInventoryData } from '../components/charts/GHGInventorySupabase';
import type { GHGInventoryResult } from '../components/charts/ghgInventoryChartTypes';
import { fetchConsumptionBasedGHGData } from './consumptionBasedGHGService';
import { fetchInventoryComparisonData } from './inventoryComparisonService';
import { supabaseClient } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';

function slugColumn(label: string): string {
  return label
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .toLowerCase();
}

export function flattenSectorGhgInventory(location: string, result: GHGInventoryResult): Record<string, unknown>[] {
  const years = result.chartData.labels as number[];
  return years.map((year, yearIndex) => {
    const row: Record<string, unknown> = { location, year };
    let total = 0;
    for (const dataset of result.chartData.datasets) {
      const value = Number(dataset.data[yearIndex]) || 0;
      row[slugColumn(dataset.label)] = value;
      total += value;
    }
    row.total_mt_co2e = total;
    return row;
  });
}

export function flattenConsumptionGhgInventory(
  location: string,
  result: Awaited<ReturnType<typeof fetchConsumptionBasedGHGData>>
): Record<string, unknown>[] {
  const years = result.chartData.labels as number[];
  return years.map((year, yearIndex) => {
    const row: Record<string, unknown> = { location, year };
    let total = 0;
    for (const dataset of result.chartData.datasets) {
      const value = Number(dataset.data[yearIndex]) || 0;
      row[slugColumn(dataset.label)] = value;
      total += value;
    }
    row.total_mt_co2e = total;
    return row;
  });
}

export function flattenInventoryComparison(
  location: string,
  result: Awaited<ReturnType<typeof fetchInventoryComparisonData>>
): Record<string, unknown>[] {
  const years = result.chartData.labels as number[];
  return years.map((year, yearIndex) => ({
    location,
    year,
    sector_based_mt_co2e: result.chartData.datasets[0]?.data[yearIndex] ?? null,
    consumption_based_mt_co2e: result.chartData.datasets[1]?.data[yearIndex] ?? null,
  }));
}

export async function loadSectorGhgInventoryDownloadRows(location: string) {
  const result = await fetchGHGInventoryData(location);
  const rows = flattenSectorGhgInventory(location, result);

  // Enrich rows with wastewater metadata from ghg_inventory_precalculated.
  // The chart dataset includes wastewater as the 'wastewater' column but does not
  // carry tier, review_required, per_capita, or the authoritative inclusive total.
  try {
    const geofips = await supabaseLocationService.getGeofips(location);
    if (geofips) {
      const years = rows.map((r) => r.year as number);
      const { data: wwMeta } = await supabaseClient
        .from('ghg_inventory_precalculated')
        .select(
          'year,wastewater_emissions,wastewater_emissions_per_capita,wastewater_data_tier,wastewater_review_required,total_emissions_with_wastewater'
        )
        .eq('geofips', geofips)
        .in('year', years);

      if (wwMeta && wwMeta.length > 0) {
        const metaByYear = new Map(wwMeta.map((r) => [r.year as number, r]));
        for (const row of rows) {
          const meta = metaByYear.get(row.year as number);
          // wastewater_emissions is already present as 'wastewater' from chart dataset;
          // add the canonical precalc value and all metadata fields explicitly.
          row.wastewater_emissions = meta?.wastewater_emissions ?? null;
          row.wastewater_emissions_per_capita = meta?.wastewater_emissions_per_capita ?? null;
          row.wastewater_data_tier = meta?.wastewater_data_tier ?? null;
          row.wastewater_review_required = meta?.wastewater_review_required ?? null;
          row.total_emissions_with_wastewater = meta?.total_emissions_with_wastewater ?? null;
        }
      }
    }
  } catch {
    // Non-fatal: metadata enrichment failed; rows still contain chart-derived wastewater column.
  }

  return rows;
}

export async function loadConsumptionGhgInventoryDownloadRows(location: string) {
  const result = await fetchConsumptionBasedGHGData(location);
  return flattenConsumptionGhgInventory(location, result);
}

export async function loadInventoryComparisonDownloadRows(location: string) {
  const result = await fetchInventoryComparisonData(location);
  return flattenInventoryComparison(location, result);
}
